import React, {useCallback, useEffect, useState} from 'react';
import {View, StyleSheet, ScrollView} from 'react-native';
import {
  Text, ActivityIndicator,
  Dialog, Portal, Button, IconButton, Snackbar, Divider, Card,
} from 'react-native-paper';
import DraggableFlatList, {ScaleDecorator} from 'react-native-draggable-flatlist';
import {useTranslation} from 'react-i18next';
import GradientHeader from '../../components/common/GradientHeader';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useMeasurementStore} from '../../store/useMeasurementStore';
import {colors, spacing} from '../../theme';

const CatalogItemOptionTypesScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {catalogItemId, catalogItemName} = route.params;

  const {
    catalogItemOptionTypes, isLoading,
    fetchOptionTypesForCatalogItem, toggleOptionType, reorderOptionTypes,
    clearCatalogItemOptionTypes,
  } = useMeasurementStore();

  const [refreshing, setRefreshing] = useState(false);
  const [optionsVisible, setOptionsVisible] = useState(false);
  const [selectedOptionType, setSelectedOptionType] = useState(null);
  const [snackbar, setSnackbar] = useState('');

  const loadData = useCallback(async () => {
    try {
      await fetchOptionTypesForCatalogItem(catalogItemId);
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    }
  }, [catalogItemId, fetchOptionTypesForCatalogItem, t]);

  useEffect(() => {
    loadData();
    return () => clearCatalogItemOptionTypes();
  }, [loadData, clearCatalogItemOptionTypes]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  }, [loadData]);

  const handleToggle = useCallback(async (item) => {
    try {
      await toggleOptionType(catalogItemId, item.optionTypeId, !item.selected);
      await loadData();
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    }
  }, [catalogItemId, toggleOptionType, loadData, t]);

  const handleViewOptions = useCallback((item) => {
    setSelectedOptionType(item);
    setOptionsVisible(true);
  }, []);

  const closeOptions = useCallback(() => {
    setOptionsVisible(false);
    setSelectedOptionType(null);
  }, []);

  const handleDragEnd = useCallback(async ({data}) => {
    const selectedItems = data.filter(item => item.selected && item.id);
    if (selectedItems.length === 0) return;

    const orderedIds = selectedItems.map(item => item.id);
    try {
      await reorderOptionTypes(catalogItemId, orderedIds);
      await loadData();
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    }
  }, [catalogItemId, reorderOptionTypes, loadData, t]);

  const renderItem = useCallback(({item, drag, isActive}) => {
    return (
      <ScaleDecorator>
        <View style={[styles.row, isActive && styles.rowDragging]}>
          {item.selected && (
            <IconButton
              icon="drag"
              size={22}
              iconColor={colors.textSecondary}
              onLongPress={drag}
              delayLongPress={150}
              style={styles.dragHandle}
            />
          )}
          {!item.selected && <View style={styles.dragPlaceholder} />}

          <IconButton
            icon={item.selected ? 'check-circle' : 'circle-outline'}
            size={24}
            iconColor={item.selected ? colors.primary : colors.textSecondary}
            onPress={() => handleToggle(item)}
            style={styles.toggleIcon}
          />

          <View style={styles.featureInfo}>
            <Text style={[styles.featureName, !item.selected && styles.featureNameUnselected]}>
              {item.optionTypeName}
            </Text>
            <Text style={styles.featureType}>{item.renderType}</Text>
          </View>

          <IconButton
            icon="dots-vertical"
            size={20}
            iconColor={colors.textSecondary}
            onPress={() => handleViewOptions(item)}
            style={styles.actionBtn}
          />
        </View>
      </ScaleDecorator>
    );
  }, [handleToggle, handleViewOptions]);

  const keyExtractor = useCallback(
    item => `${item.optionTypeId}`,
    [],
  );
  const ItemSeparator = useCallback(() => <Divider />, []);

  const ListHeader = useCallback(() => (
    <View>
      <View style={styles.headerRow}>
        <Text style={styles.headerTitle}>
          {t('measurement.optionTypesFor', {name: catalogItemName})}
        </Text>
      </View>
      <Divider />
    </View>
  ), [catalogItemName, t]);

  const ListEmpty = useCallback(() => (
    <Text style={styles.emptyText}>{t('common.noData')}</Text>
  ), [t]);

  const options = selectedOptionType?.options || [];

  if (isLoading && catalogItemOptionTypes.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <GradientHeader title={catalogItemName} onBack={() => navigation.goBack()} />

      <DraggableFlatList
        data={catalogItemOptionTypes}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        onDragEnd={handleDragEnd}
        ListHeaderComponent={ListHeader}
        ListEmptyComponent={ListEmpty}
        ItemSeparatorComponent={ItemSeparator}
        refreshing={refreshing}
        onRefresh={handleRefresh}
        activationDistance={0}
      />

      <Portal>
        <Dialog visible={optionsVisible} onDismiss={closeOptions} style={styles.optionsDialog}>
          <Dialog.Title>{selectedOptionType?.optionTypeName || t('optionType.options')}</Dialog.Title>
          <Dialog.ScrollArea style={styles.scrollArea}>
            <ScrollView>
              {options.length === 0 ? (
                <Text style={styles.noOptionsText}>{t('optionType.noOptions')}</Text>
              ) : (
                <View style={styles.optionsGrid}>
                  {options.map(option => (
                    <Card key={option.id} style={styles.optionCard}>
                      <Card.Content style={styles.optionContent}>
                        <View style={styles.optionIconPlaceholder}>
                          <Text style={styles.optionIconText}>
                            {option.name.charAt(0).toUpperCase()}
                          </Text>
                        </View>
                        <Text style={styles.optionName} numberOfLines={2}>
                          {option.name}
                        </Text>
                      </Card.Content>
                    </Card>
                  ))}
                </View>
              )}
            </ScrollView>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={closeOptions}>{t('common.done')}</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  headerRow: {padding: spacing.md, backgroundColor: colors.surface},
  headerTitle: {fontSize: 14, color: colors.textSecondary, lineHeight: 20},
  emptyText: {textAlign: 'center', color: colors.textSecondary, marginTop: spacing.xxl},
  row: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: colors.surface,
    paddingVertical: spacing.xs, paddingRight: spacing.xs,
  },
  rowDragging: {
    backgroundColor: colors.background, elevation: 5,
    shadowColor: '#000', shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.25, shadowRadius: 4,
  },
  dragHandle: {margin: 0, marginLeft: spacing.xs},
  dragPlaceholder: {width: 46, marginLeft: spacing.xs},
  toggleIcon: {margin: 0},
  featureInfo: {flex: 1, marginLeft: spacing.xs},
  featureName: {fontSize: 16, fontWeight: '600', color: colors.text},
  featureNameUnselected: {color: colors.textSecondary},
  featureType: {fontSize: 12, color: colors.textSecondary, marginTop: 1},
  actionBtn: {margin: 0},
  optionsDialog: {maxHeight: '80%', borderRadius: 15},
  scrollArea: {paddingHorizontal: 0},
  noOptionsText: {textAlign: 'center', color: colors.textSecondary, padding: spacing.xl},
  optionsGrid: {flexDirection: 'row', flexWrap: 'wrap', padding: spacing.md, gap: spacing.sm},
  optionCard: {width: '30%', backgroundColor: colors.surface, borderRadius: 8},
  optionContent: {alignItems: 'center', paddingVertical: spacing.sm, paddingHorizontal: spacing.xs},
  optionIconPlaceholder: {
    width: 48, height: 48, borderRadius: 24,
    backgroundColor: colors.primary + '15',
    justifyContent: 'center', alignItems: 'center',
    marginBottom: spacing.xs,
  },
  optionIconText: {fontSize: 20, fontWeight: 'bold', color: colors.primary},
  optionName: {textAlign: 'center', color: colors.text, fontSize: 11},
});

export default CatalogItemOptionTypesScreen;
